import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  TextInput,
  Button,
  Tabs,
  Switch,
  Space,
  NumberInput,
  Table,
  Grid,
  Text,
  Stack,
} from '@mantine/core';
import MantineTable from '../../../../common/table/mantine';
import { axios } from '../../../../utils/axios';
import { useParams } from 'react-router-dom';
import { IconRefresh, IconTrash } from '@tabler/icons-react';
import { CostingRequestsContext, useCrStatusCancelled } from '../../../../contexts/CostingRequest';
import { showAlert } from '../../../../utils/alerts';
import { ProjectOverview } from '../projectOverview';
import ContentPaper from '../../../../components/layouts/rfs/Content';
import styled from '@emotion/styled';
import { IconPlus } from '@tabler/icons-react';
import { formatSkus, checkIfFlagged, checkZeroQty } from './helper';
import { createStyles } from '@mantine/core';
import { AppLoader } from '../../../../components/common/loader';
import { NotificationModal } from '../../../../components/modal/NotificationModal';
import { RefBadge } from '../normsCheck/refBadge';
import { notificationPopup } from '../../../../constants';
import { AddChangeNumberPopup } from '../normsCheck/helper';
import moment from 'moment';

const FooterContainer = styled.div`
  float: right;
  button {
    width: 200px;
  }
`;

const CenterItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const initialData = {
  delete_component: [],
  update_component: [],
  add_component: [],
};

export const useSwitchStyles = createStyles((theme) => ({
  input: {
    outline: '2px solid black',
    outlineOffset: '-1px',
  },
  thumb: {
    color: 'black',
    backgroundColor: 'black',
  },
  track: {
    border: '3px solid black',
    margin: '1px',
  },
}));

export const BudgetBomAdjustment = ({ setLogsModal, activeTab }) => {
  const isCancelled = useCrStatusCancelled();
  const { crNumber } = useParams();

  const { info, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const [loading, setLoading] = useState(false);
  const [switchState, setSwitchState] = useState(false);
  const [baseSkus, setBaseSkus] = useState([]);
  const [componentsDict, setComponentsDict] = useState(initialData);
  const [activePlant, setActivePlant] = useState();
  const [skuID, setSkuID] = useState();
  const { producing_plant_data = [], sku_data = [] } = info;
  const [btnDisable, setBtnDisable] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (activeTab === 'budgetBomAdjustment' && sku_data.length && producing_plant_data.length) {
      const hasAccess = isCrRoleAuthorized(activePlant);
      hasAccess ? fetch(true) : fetch(false);
      setActivePlant(producing_plant_data?.[0]?.producing_plant);
      setComponentsDict(initialData);
    }
  }, [activeTab, producing_plant_data, sku_data, info]);


  const { classes } = useSwitchStyles();
  const checkDiff = (main, reference) => {
    const diff =
      parseFloat(main) && parseFloat(reference)
        ? parseFloat(main) !== parseFloat(reference)
        : false;
    const difference = parseFloat(main) - parseFloat(reference) || 0;
    return { diff: diff, variance: parseFloat(difference).toFixed(2) };
  };

  const fetch = async (refresh = false) => {
    setLoading(true);
    try {
      if (refresh) await axios.get(`/costing-request/step/refresh-bom-adjustment/${crNumber}`);
      const { data } = await axios.get(`/costing-request/step/bom-adjustment-data/${crNumber}`);
      const skus = formatSkus(data.requestsku, data.referencesku);

      handleSubmitStatus(data.stepStatus, data?.is_vic_submission);
      setBaseSkus(skus);
      const withSkuID = data.requestsku.filter((ele) => ele.sku_id);
      setSkuID(withSkuID?.[0]?.sku_id);
    } catch (e) {
      console.log(e);
      showAlert('no data available', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStatus = (step, vicSubStatus, plant = activePlant) => {
    if (!plant) plant = producing_plant_data?.[0]?.producing_plant;

    if (Array.isArray(step) && step.length > 0) {
      const activePlantId = producing_plant_data.filter(
        (ele) => ele.producing_plant === String(plant),
      )?.[0]?.id;
      const stepsData = step.filter((ele) => ele.producing_plant_id === activePlantId) || [];
      const vicState = vicSubStatus.filter(
        (ele) => ele?.int_cr_producing_plant?.producing_plant === String(plant),
      );
      if (Array.isArray(stepsData)) {
        const isStepInProgress =
          stepsData?.[0]?.status === 'In Progress' || vicState?.[0]?.status === 'In Progress';

        const hasAccess = isCrRoleAuthorized(String(plant));

        setBtnDisable(!(isStepInProgress && hasAccess));
      }
      return;
    }
  };

  const handleMaterialAdd = (data) => {
    delete data.PrimaryRefSkuCostEstimation;

    // Get SKU ID from existing subRows
    const withSkuID = baseSkus.flatMap((group) => group.subRows ?? []).filter((ele) => ele.sku_id);
    const skuID = withSkuID?.[0]?.sku_id;

    const finalData = {
      ...data,
      isReference: false,
      hasReference: true,
      flagged: false,
      reference: data,
      is_add_component: true,
      req_plant_id: activePlant,
      sku_id: skuID,
      pp_id: producing_plant_data.find((ele) => ele.producing_plant === activePlant)?.id,
    };

    // Update componentsDict
    setComponentsDict((prevData) => ({
      ...prevData,
      add_component: [
        ...prevData.add_component.filter((comp) => comp.component !== data.component),
        finalData,
      ],
    }));

    // Update baseSkus
    setBaseSkus((prevData) => {
      const updatedBaseSkus = prevData.map((group) => {
        // Add to matching group if material_group matches, else keep group
        if (group.material_group === data.material_group) {
          return {
            ...group,
            subRows: [...(group.subRows ?? []), finalData],
          };
        }
        return group;
      });

      return updatedBaseSkus;
    });

    // Trigger submit
    handleSubmit({ add_component: [finalData], delete_component: [], update_component: [] });
  };

  const handleSubmit = async (payload) => {
    setNotificationModalOpen(true);
    const resZeroUpdate = checkZeroQty(payload.update_component);
    if (resZeroUpdate) {
      showAlert({ message: 'Some quantities are zero' }, 'error');
      return;
    }
    try {
      setSubmitLoading(true);
      await axios.post(`/costing-request/step/bom-updates/${crNumber}`, payload);
      showAlert({ message: 'Request Submitted Successfully' }, 'success');
    } catch (err) {
      showAlert(err, 'error');
    } finally {
      setComponentsDict({
        delete_component: [],
        update_component: [],
        add_component: [],
      });
      fetch(true);
      setSubmitLoading(false);
    }
  };

  const handleInputChange = (component, field, value, componentToUpdate) => {
    setBaseSkus((prevData) =>
      prevData.map((group) => {
        const updatedSubRows =
          group.subRows?.map((sub) => {
            if (
              sub.component === component &&
              sub.component_id === componentToUpdate?.component_id
            ) {
              // Prepare RequestedSkuCostEstimation update
              const updatedRefData =
                field === 'costing_relevancy'
                  ? { ...sub.RequestedSkuCostEstimation }
                  : { ...sub.RequestedSkuCostEstimation, total_value: '' };

              const updatedSub = {
                ...sub,
                RequestedSkuCostEstimation: updatedRefData,
                isChanged: true,
                [field]: value,
              };

              // Recalculate flagged status
              updatedSub.flagged = checkIfFlagged(updatedSub, sub.reference);

              return updatedSub;
            }
            return sub;
          }) || [];

        return { ...group, subRows: updatedSubRows };
      }),
    );

    // Update componentsDict
    setComponentsDict((prevDict) => {
      const updatedComponent = { ...componentToUpdate, [field]: value };
      return {
        ...prevDict,
        update_component: [
          ...prevDict.update_component.filter((comp) => comp.component !== component),
          updatedComponent,
        ],
      };
    });
  };

  const handleMaterialRemoval = (component, data) => {
    setBaseSkus(
      (prevBaseSkus) =>
        prevBaseSkus
          .map((group) => {
            // Filter out the subRow with the matching component
            const updatedSubRows =
              group.subRows?.filter((sub) => sub.component !== component) || [];
            return { ...group, subRows: updatedSubRows };
          })
          .filter((group) => (group.subRows?.length ?? 0) > 0), // Remove groups with no subRows left
    );

    // Add removed item to delete_component
    setComponentsDict((prevDict) => ({
      ...prevDict,
      delete_component: [...prevDict.delete_component, data],
    }));
  };

  const handleAutoAdjustment = () => {
    setBaseSkus((prevData) =>
      prevData.map((group) => {
        const updatedGroup = { ...group };

        // Process subRows
        updatedGroup.subRows =
          updatedGroup.subRows?.map((sub) => {
            const updatedSub = { ...sub };

            if (!sub.isReference) {
              // Auto-adjust quantity
              if (
                sub.reference?.quantity != null &&
                sub.reference.quantity !== updatedSub.quantity
              ) {
                updatedSub.quantity = sub.reference.quantity;
                updatedSub.isChanged = true;
                if (sub.RequestedSkuCostEstimation) {
                  updatedSub.RequestedSkuCostEstimation = {
                    ...sub.RequestedSkuCostEstimation,
                    total_value: '',
                  };
                }
              }

              // Auto-adjust scrap
              if (sub.reference?.scrap != null && updatedSub.scrap !== sub.reference.scrap) {
                updatedSub.scrap = sub.reference.scrap;
                updatedSub.isChanged = true;
                if (sub.RequestedSkuCostEstimation) {
                  updatedSub.RequestedSkuCostEstimation = {
                    ...sub.RequestedSkuCostEstimation,
                    total_value: '',
                  };
                }
              }

              // Auto-adjust costing_relevancy
              if (typeof sub.reference?.costing_relevancy === 'boolean') {
                updatedSub.costing_relevancy = sub.reference.costing_relevancy;
                updatedSub.isChanged = true;
              }

              // Update flagged status
              updatedSub.flagged = checkIfFlagged(updatedSub, updatedSub.reference);

              // Update componentsDict if changed
              if (updatedSub.isChanged) {
                setComponentsDict((prevDict) => ({
                  ...prevDict,
                  update_component: [
                    ...prevDict.update_component.filter(
                      (comp) => comp.component !== updatedSub.component,
                    ),
                    updatedSub,
                  ],
                }));
              }
            }

            return updatedSub;
          }) || [];

        return updatedGroup;
      }),
    );
  };

  const fetchLogs = async () => {
    try {
      const url = `/costing-request/step/bom-updates/logs/${crNumber}`;
      const response = await axios.get(url);
      if (response.status !== 200) {
        throw new Error('Failed to fetch logs');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching logs:', error);
      throw error;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'material_group_description',
        header: 'Mat Group Desc',
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        headerProps: {
          style: {
            paddingLeft: '12px', // shifts header text right by 12px
          },
        },
        Cell: ({ row }) => {
          return row.original.component ? (
            <>
              {!row.original.isReference ? (
                <CenterItem>
                  <Button
                    disabled={btnDisable || isCancelled}
                    variant='subtle'
                    color='red'
                    size='md'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMaterialRemoval(row.original?.component, row.original);
                    }}
                  >
                    <IconTrash size={16} />
                  </Button>
                </CenterItem>
              ) : (
                <CenterItem>
                  <Button
                    disabled={btnDisable || isCancelled}
                    color='green'
                    size='md'
                    onClick={(e) => handleMaterialAdd(row.original)}
                  >
                    <IconPlus size={16} />
                  </Button>
                </CenterItem>
              )}
            </>
          ) : (
            <></>
          );
        },
      },
      {
        accessorKey: 'component',
        header: 'Mat No',
        sticky: 'left',
      },
      {
        accessorKey: 'component_description',
        header: 'Mat Desc',
        sticky: 'left',
      },
      {
        accessorKey: 'item_category',
        header: 'ICt',
        grow: false,
        size: 5,
      },
      {
        accessorKey: 'component_unit',
        header: 'UOM',
        grow: false,
        size: 5,
      },
      {
        id: 'quantity',
        header: 'Qty',
        size: 300,
        Cell: ({ row }) => {
          const { diff, variance } = checkDiff(
            row.original?.quantity,
            row.original.reference?.quantity,
          );

          return (
            <Table
              style={{
                tableLayout: 'fixed',
                width: '100%',
                fontSize: '11px', // smaller text for entire table
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Var</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (!row.original.component) {
                    const subRows = row?.original?.subRows ?? [];

                    const total = row?.original?.isReference
                      ? 0
                      : subRows.reduce((sum, subrow) => sum + Number(subrow?.quantity ?? 0), 0);
                    const referenceTotal = row?.original?.isReference
                      ? subRows.reduce((sum, subrow) => sum + Number(subrow?.quantity ?? 0), 0)
                      : subRows.reduce(
                          (sum, subrow) => sum + Number(subrow?.reference?.quantity ?? 0),
                          0,
                        );
                    const variance = total - referenceTotal;

                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={Number(total)}
                            disabled
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                borderColor: diff ? 'red' : theme.colors.gray[4],
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            })}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          {referenceTotal > 0 && (
                            <NumberInput
                              precision={3}
                              value={Number(referenceTotal)}
                              disabled
                              size='xs'
                              styles={{
                                input: {
                                  height: '26px',
                                  fontSize: '11px',
                                  padding: '2px 4px',
                                },
                              }}
                            />
                          )}
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          {referenceTotal > 0 && (
                            <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                              {Math.abs(Number(variance)).toFixed(3)}
                            </Text>
                          )}
                        </td>
                      </tr>
                    );
                  } else if (row.original.isReference) {
                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }} />
                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={Number(row.original.quantity ?? 0)}
                            disabled
                            size='xs'
                            styles={{
                              input: {
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            }}
                          />
                        </td>
                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          <Text size='xs' c='red'>
                            {Math.abs(Number(row.original.quantity)).toFixed(2)}
                          </Text>
                        </td>
                      </tr>
                    );
                  } else {
                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={Number(row.original?.quantity ?? 0)}
                            disabled={row.original.isReference || btnDisable || isCancelled}
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                borderColor: diff ? 'red' : theme.colors.gray[4],
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            })}
                            onChange={(value) => {
                              const newValue = Number(value) || 0;
                              handleInputChange(
                                row.original?.component,
                                'quantity',
                                newValue,
                                row.original,
                              );
                            }}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          {row.original.hasReference && (
                            <NumberInput
                              precision={3}
                              value={Number(row.original.reference?.quantity ?? 0)}
                              disabled
                              size='xs'
                              styles={{
                                input: {
                                  height: '26px',
                                  fontSize: '11px',
                                  padding: '2px 4px',
                                },
                              }}
                            />
                          )}
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          {row.original.hasReference && (
                            <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                              {Math.abs(Number(variance)).toFixed(2)}
                            </Text>
                          )}
                        </td>
                      </tr>
                    );
                  }
                })()}
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'costing_relevancy_excel',
        header: 'Costing Relevancy',
        size: 100,
        Cell: ({ row }) => {
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%', paddingTop: '0px' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Checkbox
                      checked={row.original?.costing_relevancy ?? true}
                      readOnly
                      disabled={row.original.isReference || btnDisable || isCancelled}
                      onChange={(e) =>
                        handleInputChange(
                          row.original?.component,
                          'costing_relevancy',
                          e.currentTarget.checked,
                          row.original,
                        )
                      }
                    />
                  </td>
                  <td>
                    {row.original.hasReference && (
                      <Checkbox
                        checked={row.original?.reference?.costing_relevancy || false}
                        disabled
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'scrap',
        header: 'Scrap',
        size: 300,
        Cell: ({ row }) => {
          return (
            <Table
              style={{
                tableLayout: 'fixed',
                width: '100%',
                fontSize: '11px', // smaller text
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Var</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (!row.original.component) {
                    const subRows = row?.original?.subRows ?? [];

                    const total = row.original.isReference
                      ? 0
                      : subRows.reduce((sum, subrow) => sum + Number(subrow?.scrap ?? 0), 0);

                    const referenceTotal = row.original.isReference
                      ? subRows.reduce((sum, subrow) => sum + Number(subrow?.scrap ?? 0), 0)
                      : subRows.reduce(
                          (sum, subrow) => sum + Number(subrow?.reference?.scrap ?? 0),
                          0,
                        );

                    const variance = total - referenceTotal;

                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={Number(total)}
                            disabled
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            })}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          {referenceTotal > 0 && (
                            <NumberInput
                              precision={3}
                              value={Number(referenceTotal)}
                              disabled
                              size='xs'
                              styles={{
                                input: {
                                  height: '26px',
                                  fontSize: '11px',
                                  padding: '2px 4px',
                                },
                              }}
                            />
                          )}
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          {referenceTotal > 0 && (
                            <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                              {Math.abs(Number(variance)).toFixed(3)}
                            </Text>
                          )}
                        </td>
                      </tr>
                    );
                  } else {
                    const mainScrap = row.original.isReference
                      ? 0
                      : Number(row.original?.scrap ?? 0);
                    const refScrap = row.original.isReference
                      ? Number(row.original?.scrap ?? 0)
                      : Number(row.original?.reference?.scrap ?? 0);
                    const variance = mainScrap - refScrap;

                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={mainScrap}
                            disabled={row.original.isReference || btnDisable || isCancelled}
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            })}
                            onChange={(value) => {
                              const newValue = Number(value) || 0;
                              handleInputChange(
                                row.original?.component,
                                'scrap',
                                newValue,
                                row.original,
                              );
                            }}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          <NumberInput
                            precision={3}
                            value={refScrap}
                            disabled
                            size='xs'
                            styles={{
                              input: {
                                height: '26px',
                                fontSize: '11px',
                                padding: '2px 4px',
                              },
                            }}
                          />
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                            {Math.abs(Number(variance)).toFixed(3)}
                          </Text>
                        </td>
                      </tr>
                    );
                  }
                })()}
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'total_value_excel',
        header: 'Total Value (per HL)',
        size: 300,
        Cell: ({ row }) => {
          return (
            <Table
              style={{
                tableLayout: 'fixed',
                width: '100%',
                fontSize: '14px',
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Var</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Check if this is a group row (no component)
                  if (!row.original.component) {
                    const subRows = row?.original?.subRows ?? [];

                    const total = row.original.isReference
                      ? 0
                      : subRows.reduce(
                          (sum, subrow) =>
                            sum + Number(subrow?.RequestedSkuCostEstimation?.total_value ?? 0),
                          0,
                        );

                    const referenceTotal = row.original.isReference
                      ? subRows.reduce(
                          (sum, subrow) =>
                            sum + Number(subrow?.RequestedSkuCostEstimation?.total_value ?? 0),
                          0,
                        )
                      : subRows.reduce(
                          (sum, subrow) =>
                            sum +
                            Number(
                              subrow?.reference?.PrimaryRefSkuCostEstimation?.total_value ?? 0,
                            ),
                          0,
                        );

                    const variance = total - referenceTotal;

                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <TextInput
                            value={Number(total).toFixed(2)}
                            disabled
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                borderWidth: '1px',
                                width: '90px',
                                height: '26px',
                                fontSize: '14px',
                                padding: '2px 4px',
                              },
                            })}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          {referenceTotal > 0 && (
                            <TextInput
                              value={Number(referenceTotal).toFixed(2)}
                              disabled
                              size='xs'
                              styles={{
                                input: {
                                  width: '90px',
                                  height: '26px',
                                  fontSize: '14px',
                                  padding: '2px 4px',
                                },
                              }}
                            />
                          )}
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          {referenceTotal > 0 && (
                            <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                              {Math.abs(Number(variance)).toFixed(2)}
                            </Text>
                          )}
                        </td>
                      </tr>
                    );
                  } else {
                    // Single component row
                    const mainValue = row.original.isReference
                      ? 0
                      : Number(row.original?.RequestedSkuCostEstimation?.total_value ?? 0);
                    const refValue = row.original.isReference
                      ? Number(row.original?.RequestedSkuCostEstimation?.total_value ?? 0)
                      : Number(
                          row.original?.reference?.PrimaryRefSkuCostEstimation?.total_value ?? 0,
                        );
                    const variance = mainValue - refValue;

                    return (
                      <tr>
                        <td style={{ padding: '2px 4px' }}>
                          <TextInput
                            value={mainValue.toFixed(2)}
                            disabled={row.original.isReference || btnDisable || isCancelled}
                            size='xs'
                            styles={(theme) => ({
                              input: {
                                borderWidth: '1px',
                                width: '90px',
                                height: '26px',
                                fontSize: '14px',
                                padding: '2px 4px',
                              },
                            })}
                            onChange={(value) => {
                              const newValue = Number(value) || 0;
                              handleInputChange(
                                row.original?.component,
                                'total_value',
                                newValue,
                                row.original,
                              );
                            }}
                          />
                        </td>

                        <td style={{ padding: '2px 4px' }}>
                          <TextInput
                            value={refValue.toFixed(2)}
                            disabled
                            size='xs'
                            styles={{
                              input: {
                                width: '90px',
                                height: '26px',
                                fontSize: '14px',
                                padding: '2px 4px',
                              },
                            }}
                          />
                        </td>

                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          <Text size='xs' c={Number(variance) === 0 ? 'dimmed' : 'red'}>
                            {Math.abs(Number(variance)).toFixed(2)}
                          </Text>
                        </td>
                      </tr>
                    );
                  }
                })()}
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'curr_excel',
        header: 'Curr',
        size: 150,
        Cell: ({ row }) => {
          const mainValue = row.original?.currency;
          const refValue = row.original?.reference?.currency;

          return (
            <Table style={{ tableLayout: 'fixed', width: '100%', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '2px 4px' }}>{mainValue}</td>
                  <td style={{ padding: '2px 4px' }}>
                    {row.original.hasReference ? refValue : ''}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'price_excel',
        header: 'Price',
        size: 150,
        Cell: ({ row }) => {
          // Handle group rows
          if (!row.original.component) {
            const subRows = row?.original?.subRows ?? [];

            // Pick first available price in subRows
            const mainPrice = row.original.isReference
              ? 0
              : subRows.find(
                  (sub) =>
                    sub?.RequestedSkuCostEstimation?.price != null ||
                    sub?.RequestedSkuCostEstimation?.Price != null,
                )?.RequestedSkuCostEstimation?.price ??
                subRows.find(
                  (sub) =>
                    sub?.RequestedSkuCostEstimation?.price != null ||
                    sub?.RequestedSkuCostEstimation?.Price != null,
                )?.RequestedSkuCostEstimation?.Price ??
                '';

            const refPrice = row.original.isReference
              ? subRows.find(
                  (sub) =>
                    sub?.RequestedSkuCostEstimation?.price != null ||
                    sub?.RequestedSkuCostEstimation?.Price != null,
                )?.RequestedSkuCostEstimation?.price ??
                subRows.find(
                  (sub) =>
                    sub?.RequestedSkuCostEstimation?.price != null ||
                    sub?.RequestedSkuCostEstimation?.Price != null,
                )?.RequestedSkuCostEstimation?.Price ??
                ''
              : subRows.find(
                  (sub) =>
                    sub?.reference?.PrimaryRefSkuCostEstimation?.price != null ||
                    sub?.reference?.PrimaryRefSkuCostEstimation?.Price != null,
                )?.reference?.PrimaryRefSkuCostEstimation?.price ??
                subRows.find(
                  (sub) =>
                    sub?.reference?.PrimaryRefSkuCostEstimation?.price != null ||
                    sub?.reference?.PrimaryRefSkuCostEstimation?.Price != null,
                )?.reference?.PrimaryRefSkuCostEstimation?.Price ??
                '';

            return (
              <Table
                style={{
                  tableLayout: 'fixed',
                  width: '100%',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                    <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>{mainPrice}</td>
                    <td style={{ padding: '2px 4px' }}>{refPrice}</td>
                  </tr>
                </tbody>
              </Table>
            );
          } else {
            // Single component row
            const mainPrice = row.original.isReference
              ? 0
              : row.original?.RequestedSkuCostEstimation?.price ??
                row.original?.RequestedSkuCostEstimation?.Price ??
                '';
            const refPrice = row.original.isReference
              ? row.original?.RequestedSkuCostEstimation?.price ??
                row.original?.RequestedSkuCostEstimation?.Price ??
                ''
              : row.original?.reference?.PrimaryRefSkuCostEstimation?.price ??
                row.original?.reference?.PrimaryRefSkuCostEstimation?.Price ??
                '';

            return (
              <Table style={{ tableLayout: 'fixed', width: '100%', fontSize: '14px' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                    <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>{mainPrice}</td>
                    <td style={{ padding: '2px 4px' }}>{refPrice}</td>
                  </tr>
                </tbody>
              </Table>
            );
          }
        },
      },
      {
        id: 'price_unit_excel',
        header: 'Price Unit',
        size: 150,
        Cell: ({ row }) => {
          const mainValue = row.original?.RequestedSkuCostEstimation?.un;
          const refValue = row.original?.reference?.PrimaryRefSkuCostEstimation?.un;

          return (
            <Table style={{ tableLayout: 'fixed', width: '100%', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Main</th>
                  <th style={{ padding: '2px 4px', textAlign: 'center' }}>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '2px 4px' }}>{mainValue}</td>
                  <td style={{ padding: '2px 4px' }}>
                    {row.original.hasReference ? refValue : ''}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
    ],
    [baseSkus, activePlant],
  );

  const Footer = () => (
    <FooterContainer>
      <Button
        onClick={() => handleSubmit(componentsDict)}
        variant='gradient'
        radius='md'
        size='md'
        fullWidth
        disabled={btnDisable || isCancelled || submitLoading}
      >
        {submitLoading ? <AppLoader size='sm' /> : 'Submit'}
      </Button>
    </FooterContainer>
  );
  const rightSideBtns = [
    {
      button: (
        <Button
          color='dark'
          disabled={btnDisable || isCancelled}
          onClick={() => setOpened(true)}
        >
          Change Number
        </Button>
      ),
    },
    {
      button: (
        <Button
          color='dark'
          onClick={async () => {
            try {
              const logs = await fetchLogs();
              setLogsModal({
                open: true,
                logs: logs,
                type: 'Budget BOM Adjustment Check',
              });
            } catch (error) {
              console.error('Error fetching logs:', error);
            }
          }}
        >
          View Logs
        </Button>
      ),
    },
    {
      button: (
        <Button
          disabled={btnDisable || isCancelled}
          variant='light'
          color='gray.0'
          onClick={() => fetch(true)}
        >
          <IconRefresh color='black' variant='filled' size={16} />
        </Button>
      ),
    },
    {
      button: (
        <Button
          variant='light'
          size='sm'
          color='blue.3'
          disabled={btnDisable || isCancelled}
          styles={(theme) => ({
            label: {
              color: theme.colors.blue[6],
            },
          })}
          onClick={() => handleAutoAdjustment()}
        >
          Auto Adjustment of BOM
        </Button>
      ),
    },
  ];

  const getExportData = () => {
    let dataToExport = [];

    const skus = baseSkus.filter((item) => item?.producingPlant?.producing_plant === activePlant);

    skus.forEach((data) => {
      const {
        componentNumber,
        component_description,
        item_category,
        component_unit,
        quantity,
        costing_relevancy,
        scrap,
        reference,
        RequestedSkuCostEstimation,
        currency,
      } = data;
      const { variance: qtyVariance } = checkDiff(quantity, reference?.quantity);

      const { variance: scrapVariance } = checkDiff(scrap, reference?.scrap);

      const { variance: totalValueVariance } = checkDiff(
        RequestedSkuCostEstimation?.total_value,
        reference?.PrimaryRefSkuCostEstimation?.total_value,
      );

      const refPrice =
        reference?.PrimaryRefSkuCostEstimation?.price ||
        reference?.PrimaryRefSkuCostEstimation?.Price;
      const mainPrice = RequestedSkuCostEstimation?.price || RequestedSkuCostEstimation?.Price;
      const { variance: priceVariance } = checkDiff(mainPrice, refPrice);

      dataToExport.push({
        'Mat No': componentNumber,
        'Mat Desc': component_description,
        ICt: item_category,
        UOM: component_unit,
        'Qty Main': quantity,
        'Qty Ref': reference?.quantity,
        'Qty Var': Math.abs(parseFloat(qtyVariance)),
        'Costing Relevancy Main': costing_relevancy ? 'Yes' : 'No',
        'Costing Relevancy Ref': reference?.costing_relevancy ? 'Yes' : 'No',
        'Scrap Main': scrap,
        'Scrap Ref': reference?.scrap,
        'Scrap Var': Math.abs(parseFloat(scrapVariance)),
        'Total Value (per HL) Main': RequestedSkuCostEstimation?.total_value,
        'Total Value (per HL) Ref': reference?.PrimaryRefSkuCostEstimation?.total_value,
        'Total Value (per HL) Var': Math.abs(parseFloat(totalValueVariance)),
        'Curr Main': currency,
        'Curr Ref': reference?.currency,
        'Price Main': mainPrice,
        'Price Ref': refPrice,
        'Price Var': Math.abs(parseFloat(priceVariance)),
        'Price Unit Main': RequestedSkuCostEstimation?.un,
        'Price Unit Ref': reference?.PrimaryRefSkuCostEstimation?.un,
        'Price Per Unit Main': RequestedSkuCostEstimation?.price_unit,
        'Price Per Unit Ref': reference?.PrimaryRefSkuCostEstimation?.price_unit,
      });
    });

    dataToExport = dataToExport.map((row) => {
      const sanitizedRow = {};
      for (const key in row) {
        sanitizedRow[key] = row[key] ?? '';
      }
      return sanitizedRow;
    });

    return dataToExport;
  };

  const leftButtons = [
    {
      button: (
        <Switch
          label='View Flagged Data'
          onChange={(event) => {
            setSwitchState(event.target.checked);
          }}
          classNames={{ input: classes.input, thumb: classes.thumb, track: classes.track }}
        />
      ),
    },
  ];
  const enrichedBaseSkus = useMemo(
    () =>
      baseSkus.map((item) => ({
        ...item,
        rowId: `${item.producingPlant.pp_id}_${item.material_group}`,
        subRows: item.subRows?.map((sub) => ({
          ...sub,
          rowId: sub.component_id, // already unique
        })),
      })),
    [baseSkus],
  );

  const initialState = {
    columnPinning: {
      left: ['actions', 'componentNumber', 'component_description'],
      right: [],
    },
    sorting: [
      {
        id: 'component_description',
        desc: false,
      },
    ],
  };

  return (
    <>
      {opened && (
        <AddChangeNumberPopup
          opened={opened}
          onClose={() => setOpened(false)}
          crNumber={crNumber}
          isBom={true}
        />
      )}
      <ContentPaper footer={<Footer />}>
        <ProjectOverview />
        <Space h='sm' />
        <Tabs value={activePlant} onTabChange={(value) => setActivePlant(value)} color='yellow'>
          <Tabs.List>
            {producing_plant_data.map(({ producing_plant }, index) => (
              <Tabs.Tab value={producing_plant} className='menu-tabs' key={index}>
                {producing_plant}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {producing_plant_data.map(({ producing_plant }, index) => {
            return (
              <Tabs.Panel value={producing_plant} key={index}>
                <MantineTable
                  columns={columns}
                  initialData={enrichedBaseSkus.filter(
                    (item) =>
                      (item?.producingPlant?.producing_plant === producing_plant ||
                        item.isReference) &&
                      (!switchState ||
                        (switchState && (item?.subRows?.some((sub) => sub.flagged) ?? false))),
                  )}
                  enablePinning
                  initialState={{
                    columnPinning: { left: ['mrt-row-expand'] },
                  }}
                  enableRowSelection={false}
                  loading={loading}
                  unique='rowId'
                  showSelectedToggle={false}
                  hideSelectColumn
                  showResetAll={false}
                  enableExport
                  getExportData={getExportData}
                  enableExpanding={true}
                  positionExpandColumn={'first'}
                  enablePagination={false}
                  enableBottomToolbar={false}
                  enableSelectAll={false}
                  rightButtons={rightSideBtns}
                  leftButtons={leftButtons}
                  headerBadge={<RefBadge crNumber={crNumber} activePlant={activePlant} />}
                  csvFilename={`${activePlant}_budget_bom_adjustment_${moment().format(
                    'YYYY-MM-DD',
                  )}`}
                  mantineTableProps={{
                    style: { fontSize: '14px' },
                    highlightOnHover: false,
                    withBorder: true,
                    withColumnBorders: true,
                  }}
                  mantineTableHeadCellProps={{
                    style: {
                      textAlign: 'center',
                      padding: '4px 6px',
                      fontSize: '14px',
                    },
                  }}
                  mantineTableBodyCellProps={{
                    style: {
                      padding: '2px 6px',
                      fontSize: '14px',
                    },
                  }}
                  mantineTableBodyRowProps={{
                    style: { height: '24px' },
                  }}
                  mantinePaperProps={{
                    style: { padding: 0, margin: 0, boxShadow: 'none' },
                  }}
                />
              </Tabs.Panel>
            );
          })}
        </Tabs>
      </ContentPaper>
      <NotificationModal
        notificationModalOpen={notificationModalOpen}
        setNotificationModalOpen={setNotificationModalOpen}
        notificationText={notificationPopup.text}
        title={notificationPopup.title}
      />
    </>
  );
};
